import styled from 'astroturf/react'
import { Button } from '@calendly/ui/components/button'
import PropTypes from 'prop-types'
import { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import BookingField from 'components/enter_details/booking_field'
import { useI18n } from 'locales'
import TextField from 'components/form/text'
import { useTrackCommerce } from 'hooks/useTrackCommerce'
import { anonymousIdSelector } from 'selectors/segment'
import { useValidateCouponMutation } from 'root-api'
import { useEventType } from 'components/use_event_type'
import { couponErrorSelector, stripeClientSecretSelector } from 'selectors/payment'
import { resetCouponError } from 'actions/scheduling'
import ErrorAlert from 'components/error/alert'
import { toLowerCase } from 'utils/strings'
import _CouponSection from "coupon-CouponSection.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/enter_details/payment/coupon.js?styleId=CouponSection";
import _ApplyButton from "coupon-ApplyButton.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/enter_details/payment/coupon.js?styleId=ApplyButton";



const CouponSection = /*#__PURE__*/styled('div', null, {
  displayName: "CouponSection",
  styles: _CouponSection
})

const ApplyButton = /*#__PURE__*/styled(Button, null, {
  displayName: "ApplyButton",
  styles: _ApplyButton
})

const couponErrorCode = (error) => {
  if (!error) return undefined
  if (error.payload?.message === 'The supplied parameters are invalid.') {
    const details = error.payload.details
    const errorAttributes = ['code', 'booking_code', 'invitee_email']
    const codeError = details.find((detail) => errorAttributes.includes(detail.parameter))
    if (codeError) {
      const parameter = codeError.parameter
      const code = codeError.code
      // Validate uses "code" instead of "booking_code", consolidate for consistency in analytics and parsing
      return `${parameter}:${code}`.replace('booking_code:', 'code:')
    }
  }
  return 'default:invalid'
}

const allowedErrorCodes = [
  'code:invalid',
  'code:max_redemptions',
  'code:max_per_invitee',
  'code:no_longer_valid',
  'invitee_email:required',
  'default:invalid'
]

const isServerError = (error) => error?.status >= 500 || error?.originalStatus >= 500 || error?.type === 'server'

export const couponErrorAnalyticsText = (error) => {
  const errorCode = couponErrorCode(error)

  if (allowedErrorCodes.includes(errorCode)) {
    return errorCode.replaceAll(':', '_')
  }
  return 'default_invalid'
}

const couponErrorText = (error, i18n) => {
  // Don't show field-level errors for server errors - those get handled by ErrorAlert
  if (isServerError(error)) {
    return ''
  }

  const errorCode = couponErrorCode(error)
  if (!errorCode) return ''

  const i18nKey = (() => {
    switch (errorCode) {
      case 'code:invalid':
        return 'booking.templates.payment.coupon.errors.invalid'
      case 'code:max_redemptions':
      case 'code:max_per_invitee':
        return 'booking.templates.payment.coupon.errors.max_redemptions'
      case 'code:no_longer_valid':
        return 'booking.templates.payment.coupon.errors.no_longer_valid'
      case 'invitee_email:required':
        return 'booking.templates.payment.coupon.errors.email_required'
      default:
        return 'booking.templates.payment.coupon.errors.invalid'
    }
  })()
  return i18n.t(i18nKey)
}

const applyCouponProperties = (code, couponData, eventType, anonymousId) => ({
  invitee_anonymous_id: anonymousId,
  event_type_uuid: eventType.uuid,
  action: 'apply',
  booking_code_valid: true,
  booking_code: code,
  discount_type: toLowerCase(couponData.booking_code_type),
  fixed_amount_cents: couponData.fixed_amount_cents,
  percent_off: couponData.percent_off,
  currency: couponData.currency,
})

const applyCouponErrorProperties = (code, error, eventType, anonymousId) => ({
  invitee_anonymous_id: anonymousId,
  event_type_uuid: eventType.uuid,
  action: 'apply',
  booking_code_valid: false,
  booking_code: code,
  discount_type: null,
  fixed_amount_cents: null,
  percent_off: null,
  currency: null,
  error_reason: couponErrorAnalyticsText(error),
})

const Coupon = ({ prefilledCouponCode, onCouponApplied, inviteeEmail }) => {
  const { i18n } = useI18n()
  const [couponCode, setCouponCode] = useState(prefilledCouponCode || '')
  const [serverError, setServerError] = useState(null)
  const track = useTrackCommerce()
  const eventType = useEventType()
  const anonymousId = useSelector(anonymousIdSelector)
  const stripeClientSecret = useSelector(stripeClientSecretSelector)
  const dispatch = useDispatch()
  const couponSubmissionError = useSelector(couponErrorSelector)
  const couponRef = useRef(null)
  const [
    validateCoupon,
    { error: couponErrorData, isLoading: couponLoading }
  ] = useValidateCouponMutation({
    fixedCacheKey: 'couponData'
  })

  let couponError = couponErrorText(couponErrorData, i18n)

  if (couponSubmissionError.error) {
    couponError = couponErrorText(couponSubmissionError.data, i18n)
  }

  // Auto-scroll into view when there's an error
  useEffect(() => {
    if (couponSubmissionError.error && couponRef.current) {
      couponRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }, [couponSubmissionError.error])

  // Restore form data from URL query params on page load (except booking_code)
  useEffect(() => {
    // Skip if window.location.href is not available (e.g., in tests)
    if (!window.location.href) return

    const urlParams = new URLSearchParams(window.location.search)
    const hasFormData = Array.from(urlParams.keys()).some((key) => key !== 'booking_code')

    if (hasFormData) {
      urlParams.forEach((value, name) => {
        if (name !== 'booking_code' && name !== 'redeem') {
          // Use querySelector for form restoration as it's accessing all form elements
          const input = document.querySelector(`[name="${name}"]`)
          if (input) {
            input.value = value
            // Trigger change event for React controlled components
            const event = new Event('input', { bubbles: true })
            input.dispatchEvent(event)
          }
        }
      })
    }
  }, [])

  const applyCoupon = async () => {
    const result = await validateCoupon({
      code: couponCode,
      inviteeEmail: inviteeEmail || null,
      eventTypeUuid: eventType.uuid,
      clientSecret: stripeClientSecret.value
    })

    if (!result.error) {
      track('Booking Code Interaction',
        applyCouponProperties(couponCode, result.data.resource, eventType, anonymousId))
      onCouponApplied(couponCode)
    } else {
      // Check if it's a server error (500+)
      if (isServerError(result.error)) {
        setServerError(result.error)
      }


      track('Booking Code Interaction',
        applyCouponErrorProperties(couponCode, result.error, eventType, anonymousId))
    }
  }

  const handleCouponChange = (e) => {
    const value = e.target.value.toUpperCase()
    dispatch(resetCouponError())
    setCouponCode(value)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (couponCode.trim().length > 0) {
        applyCoupon()
      }
    }
  }

  const preserveFormDataAndReload = () => {
    // Skip if window.location.href is not available (e.g., in tests)
    if (!window.location.href) {
      window.location.reload?.()
      return
    }

    // Get current URL and add form data as query params
    const url = new URL(window.location.href)
    // Use querySelectorAll for form data preservation as it needs to access all form elements
    const forms = document.querySelectorAll('input, select, textarea')

    forms.forEach((input) => {
      if (input.name && input.name !== 'booking_code' && input.value) {
        // Add form field values to URL params
        url.searchParams.set(input.name, input.value)
      }
    })

    // Reload with the updated URL containing form data
    window.location.href = url.toString()
  }

  const dismissServerError = () => {
    setServerError(null)
  }

  return (
    <BookingField>
      <CouponSection ref={couponRef}>
        <TextField
          label={i18n.t('booking.templates.payment.coupon.label')}
          value={couponCode}
          onChange={handleCouponChange}
          placeholder={i18n.t('booking.templates.payment.coupon.placeholder')}
          error={couponError || ''}
          onKeyDown={handleKeyDown}
        />
        <ApplyButton
          onClick={applyCoupon}
          disabled={!couponCode.trim() || couponLoading}
          decoration="outline"
          loading={couponLoading}
          size="small"
        >
          {i18n.t('booking.templates.payment.coupon.apply')}
        </ApplyButton>
      </CouponSection>

      <ErrorAlert
        error={serverError}
        title={i18n.t('booking.templates.payment.coupon.errors.server_error_title')}
        text={i18n.t('booking.templates.payment.coupon.errors.server_error_message')}
        buttonText={i18n.t('booking.templates.payment.coupon.errors.try_again')}
        buttonClick={preserveFormDataAndReload}
        dismissError={dismissServerError}
      />
    </BookingField>
  )
}

Coupon.propTypes = {
  onCouponApplied: PropTypes.func.isRequired,
  prefilledCouponCode: PropTypes.string,
  inviteeEmail: PropTypes.string,
}

export default Coupon
